import React from "react";
import PropTypes from "prop-types";
import BackgroundImage from "core-web/components/BackgroundImage";
import Breadcrumbs from "theme/components/Breadcrumbs";
import TextStyles from "theme/components/text/TextStyles";
import Wysiwyg from "theme/libs/wordpress/components/Wysiwyg";
import styled from "theme/libs/styled";

const validTags = ["url", "i", "b", "u", "ul", "ol", "li", "span"];

const Wrapper = styled("div")`
    padding-top: 16px;
    padding-bottom: 24px;

    ${({ theme }) => theme.above.tabletLg} {
        padding-bottom: 48px;
    }
`;

const TextRow = styled("div")`
    display: grid;
    gap: 16px;
    margin-top: 48px;
    margin-bottom: 32px;

    ${({ theme }) => theme.above.tabletLg} {
        grid-template-columns: 2fr 1fr;
        gap: 40px;
        align-items: start;
        margin-top: 64px;
        margin-bottom: 48px;
    }
`;

const TitleCol = styled("div")``;

const ContentCol = styled("div")`
    display: flex;
    flex-direction: column;
    gap: 12px;
    align-items: flex-start;
`;

const Metadata = styled("div")`
    opacity: 0.6;
`;

const ImageWrapper = styled("div")`
    border-radius: 12px;
    overflow: hidden;
    aspect-ratio: 16 / 9;

    ${({ theme }) => theme.below.tabletLg} {
        aspect-ratio: 4 / 3;
    }
`;

const ArticleDefaultHeroBlock = ({
    breadcrumbs,
    createdShort,
    category,
    publishedAgo,
    headingText,
    paragraph,
    desktopUrl,
    mobileUrl,
}) => {
    const metadataParts = [createdShort, category, publishedAgo].filter(Boolean);

    return (
        <Wrapper>
            {breadcrumbs && <Breadcrumbs isUnderneathNavbar breadcrumbs={breadcrumbs} />}
            <TextRow>
                <TitleCol>
                    {headingText && (
                        <TextStyles
                            as="h1"
                            textStyles={["Myriad Pro/32-100-semibold", null, null, null, "Myriad Pro/48-100-semibold"]}
                        >
                            {headingText}
                        </TextStyles>
                    )}
                </TitleCol>
                <ContentCol>
                    {paragraph && paragraph.length > 0 && (
                        <div style={{ maxWidth: "min(515px, 100%)" }}>
                            <Wysiwyg
                                data={paragraph}
                                textComponent={(props) => <TextStyles textStyles="Myriad Pro/20-140" {...props} />}
                                validTags={validTags}
                            />
                        </div>
                    )}
                    {metadataParts.length > 0 && (
                        <Metadata>
                            <TextStyles textStyles="Myriad Pro/14-140">{metadataParts.join(" | ")}</TextStyles>
                        </Metadata>
                    )}
                </ContentCol>
            </TextRow>
            {(desktopUrl || mobileUrl) && (
                <ImageWrapper>
                    <BackgroundImage
                        className="article-hero-image"
                        src={[mobileUrl || desktopUrl, null, null, desktopUrl || mobileUrl]}
                        loading="eager"
                        query={{ w: [768, 1024, 1440, 1920], q: 70 }}
                    />
                </ImageWrapper>
            )}
        </Wrapper>
    );
};

ArticleDefaultHeroBlock.propTypes = {
    breadcrumbs: PropTypes.arrayOf(PropTypes.object),
    category: PropTypes.string,
    createdShort: PropTypes.string,
    desktopUrl: PropTypes.string,
    headingText: PropTypes.string,
    mobileUrl: PropTypes.string,
    paragraph: PropTypes.array,
    publishedAgo: PropTypes.string,
};

ArticleDefaultHeroBlock.defaultProps = {
    breadcrumbs: null,
    category: null,
    createdShort: null,
    desktopUrl: null,
    headingText: null,
    mobileUrl: null,
    paragraph: null,
    publishedAgo: null,
};

export default ArticleDefaultHeroBlock;
